import { type AxiosRequestConfig, type AxiosError } from 'axios';

import ApiClient from 'helpers/base-api-client';

/**
 * Represents the response structure for page data returned from the v3 REST API.
 */

export type V3ApiResponseMetaData = {
  hasNextPage: boolean;
  nextPage: string;
  hasPreviousPage: boolean;
  previousPage: string;
};

/**
 * Represents the successful response structure returned from the v3 REST API when the request is successful.
 * @template TData The type of data returned from the endpoint you are working with.
 * @example
 * type TestResponse = {
 *   test_strings: ['one', 'two', 'three'];
 * };
 *
 * getV3Test: useQuery<V3ApiSuccessfulResponse<TestResponse>, V3ApiFailedResponse>({
 *   query: () => ({ url: '/test' }),
 * }),
 */

export type V3ApiSuccessfulResponse<TData> = {
  data: TData;
  meta: V3ApiResponseMetaData;
  errors: [];
};

/**
 * Represents the response structure returned from the v3 REST API when the request has failed.
 */

// This is the shape of faild responses that the BE will return
// https://github.com/CompanyCam/Company-Cam-API/blob/master/adr/2023-12-04_REST-API-Guidelines.md#response-shapes
export type V3ApiFailedResponse = {
  data: {};
  errors: {
    status: string;
    detail: string;
  }[];
  meta: {};
};

export const transformErrorResponse = (error: any): V3ApiFailedResponse => {
  const errorData = error?.response?.data;
  // if the error response data is an array it means it's a 401 error from devise. BE can't override
  // this so we reformat it on the client
  const isDevise401Error = Array.isArray(errorData);

  if (isDevise401Error) {
    return {
      data: {},
      errors: [
        {
          status: '401',
          detail: errorData[0]?.error.general || 'Unauthorized.',
        },
      ],
      meta: {},
    };
  }

  const errors = errorData?.errors ?? [
    {
      status: '500',
      detail: 'Unknown error.',
    },
  ];

  return {
    data: {},
    errors,
    meta: {},
  };
};

const v3ApiClient = new ApiClient({ baseURL: '/v3/' });

const isUnauthenticatedError = (error: AxiosError) => {
  return error.response?.status === 401;
};

const redirectIfUnauthenticated = (error: AxiosError) => {
  if (isUnauthenticatedError(error)) {
    window.location.replace('/users/sign_in');
  }
};

// TODO: Add JSDoc for this function along with example usage

export const request = async ({
  url,
  method = 'get',
  data,
  headers,
  params,
}: AxiosRequestConfig) => {
  try {
    const response = await v3ApiClient.client.request({
      url,
      method,
      data,
      headers,
      params,
    });
    return response.data;
  } catch (e) {
    redirectIfUnauthenticated(e as AxiosError);
    // We must throw an error in order for @tanstack/react-query hooks to return the error value
    // Since our errors are returned as an array of errors, how should we show each error to the developer?
    // Should we log errors via Bugsnag?
    throw transformErrorResponse(e);
  }
};
